<template>
  <div class="gda-survey-box">
    <div>{{updateColor()}}</div>
    
    <!-- Loading Overlay -->
    <transition name="fade">
      <Loading v-if="isLoading"></Loading>
    </transition>

    <!-- Image Cropper -->
    <imageCropper
      v-if="cropperOn"
      :img="imgFile.src"
      :imgType="imgFile.type"
      stencilComponent="rectangle-stencil"
      :cropperProps="{aspectRatio: 1/1}"
      @cropConfirmed="handleCropperConfirm"
      @cropCanceled="handleCropperCancel"
    />

    <div class="content-wrapper">
      <!-- Header with Title and Buttons aligned -->
      <div class="header-row">
        <div class="section-header-inline">Game Information</div>
        <div class="header-actions">
          <button v-if="!modify" class="action-btn modify-btn" @click.prevent="handleModify">
            MODIFY
          </button>
          <template v-else>
            <button class="action-btn cancel-btn" @click.prevent="handleCancel">
              CANCEL
            </button>
            <button class="action-btn confirm-btn" @click.prevent="handleSave">
              CONFIRM
            </button>
          </template>
        </div>
      </div>

      <!-- Game Information Section -->
      <div class="fields-grid">
        <div class="field-card">
          <div class="field-label">Game Name</div>
          <input 
            v-if="modify"
            v-model="editData.gameName" 
            class="field-input" 
            placeholder="Enter game name" />
          <div v-else class="field-display">{{ detail.gameName }}</div>
        </div>

        <div class="field-card">
          <div class="field-label">Company</div>
          <input 
            v-if="modify"
            v-model="editData.company" 
            class="field-input" 
            placeholder="Enter company name" />
          <div v-else class="field-display">{{ detail.company }}</div>
        </div>

        <div class="field-card full-width">
          <div class="field-label">End Date & Time</div>
          <div v-if="modify" class="date-selection-grid">
            <div class="date-item">
              <label>Year</label>
              <select class="date-select" @change="handleSelect('year', $event)">
                <option v-for="year in assignYearSelection" :key="year.yearText" :selected="year.yearText == assignYearSelected">
                  {{ year.yearText }}
                </option>
              </select>
            </div>
            <div class="date-item">
              <label>Month</label>
              <select class="date-select" @change="handleSelect('month', $event)">
                <option v-for="month in assignMonthSelection" :key="month.monthText" :selected="month.monthString == assignMonthSelected">
                  {{ month.monthText }}
                </option>
              </select>
            </div>
            <div class="date-item">
              <label>Day</label>
              <select class="date-select" @change="handleSelect('day', $event)">
                <option v-for="day in assignDaySelection" :key="day.dayText" :selected="day.dayText == assignDaySelected">
                  {{ day.dayText }}
                </option>
              </select>
            </div>
            <div class="date-item">
              <label>Hour</label>
              <select class="date-select" @change="handleSelect('hour', $event)">
                <option v-for="(hour, idx) in assignHourSelection" :key="hour.timeText" :selected="idx == assignHourSelected">
                  {{ hour.timeText }}
                </option>
              </select>
            </div>
          </div>
          <div v-else class="field-display">{{ endTime }}</div>
        </div>

        <div class="field-card full-width">
          <div class="field-label">Description</div>
          <textarea 
            v-if="modify"
            v-model="editData.description" 
            class="field-input field-textarea" 
            placeholder="Enter game description"
            rows="4"></textarea>
          <div v-else class="field-display">{{ detail.description || 'No description provided' }}</div>
        </div>

        <div class="field-card full-width image-upload-card">
          <div class="field-label">Game Image</div>
          <div class="image-upload-section">
            <div v-if="modify" class="upload-area" @click="triggerFileInput" @mouseenter="shadeOn = true" @mouseleave="shadeOn = false">
              <input 
                ref="fileInput"
                type="file" 
                accept="image/*"
                @change="uploadImg"
                style="display: none;" />
              
              <div class="image-preview-container">
                <img :src="editData.img" alt="Game preview" class="preview-image" />
                <div v-if="shadeOn" class="image-overlay">
                  <p>Click to change image</p>
                </div>
              </div>
            </div>
            <div v-else class="image-display">
              <img :src="detail.img" alt="Game image" class="preview-image" />
            </div>
          </div>
        </div>
      </div>

      <!-- Game Tags Section -->
      <div class="section-header">Game Tags</div>
      <div class="tags-section">
        <!-- Platform Tags -->
        <div class="tag-category">
          <div class="tag-category-header">Platform</div>
          <div class="tag-options">
            <button
              v-for="platform in platformOptions"
              :key="platform"
              :class="['tag-btn', { 'selected': platformSelected.includes(platform), 'disabled': !modify }]"
              :disabled="!modify"
              @click="modify && toggleTag('platform', platform)">
              {{ platform }}
            </button>
          </div>
        </div>

        <!-- Behavior Tags -->
        <div class="tag-category">
          <div class="tag-category-header">Behavior</div>
          <div class="tag-options">
            <button
              v-for="behavior in behaviorOptions"
              :key="behavior"
              :class="['tag-btn', { 'selected': behaviorSelected.includes(behavior), 'disabled': !modify }]"
              :disabled="!modify"
              @click="modify && toggleTag('behavior', behavior)">
              {{ behavior }}
            </button>
          </div>
        </div>

        <!-- Genre Tags -->
        <div class="tag-category">
          <div class="tag-category-header">Genre</div>
          <div class="tag-options">
            <button
              v-for="genre in genreOptions"
              :key="genre"
              :class="['tag-btn', { 'selected': genreSelected.includes(genre), 'disabled': !modify }]"
              :disabled="!modify"
              @click="modify && toggleTag('genre', genre)">
              {{ genre }}
            </button>
          </div>
        </div>
      </div>

      <!-- QA Lead Information Section -->
      <div class="section-header">QA Lead Information</div>
      <div class="fields-grid">
        <div class="field-card">
          <div class="field-label">QA Lead Name</div>
          <input 
            v-if="modify"
            v-model="editData.qaLead.name" 
            class="field-input" 
            placeholder="Enter QA lead name" />
          <div v-else class="field-display">{{ getQaLeadName() }}</div>
        </div>

        <div class="field-card">
          <div class="field-label">Email</div>
          <input 
            v-if="modify"
            v-model="editData.qaLead.email" 
            class="field-input" 
            type="email"
            placeholder="Enter email address" />
          <div v-else class="field-display">{{ getQaLeadEmail() }}</div>
        </div>

        <div class="field-card">
          <div class="field-label">Work Phone</div>
          <input 
            v-if="modify"
            v-model="editData.qaLead.workPhone" 
            class="field-input" 
            type="tel"
            placeholder="Enter work phone" />
          <div v-else class="field-display">{{ getQaLeadWorkPhone() }}</div>
        </div>

        <div class="field-card">
          <div class="field-label">Mobile</div>
          <input 
            v-if="modify"
            v-model="editData.qaLead.mobile" 
            class="field-input" 
            type="tel"
            placeholder="Enter mobile number" />
          <div v-else class="field-display">{{ getQaLeadMobile() }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import imageCropper from '../layout/ImageCropper.vue'
import Loading from '../loading/index.vue'
import GamesObject from "../../Firestore/GamesObject";

const today = new Date();

function getMimeType(file, fallback = null) {
    const byteArray = (new Uint8Array(file)).subarray(0, 4);
    let header = '';
    for (let i = 0; i < byteArray.length; i++) {
        header += byteArray[i].toString(16);
    }
    switch (header) {
        case "89504e47":
            return "image/png";
        case "47494638":
            return "image/gif";
        case "ffd8ffe0":
        case "ffd8ffe1":
        case "ffd8ffe2":
        case "ffd8ffe3":
        case "ffd8ffe8":
            return "image/jpeg";
        default:
            return fallback;
    }
}

export default {
    name: "gameDetailDescription",
    components: {
        imageCropper,
        Loading,
    },
    props: {
        detail: {
            type: Object
        }
    },
    data() {
        return {
            msg: "gameDetailDescription",
            userType: window.localStorage.getItem("userType") ? parseInt(window.localStorage.getItem("userType")) : -1,
            modify: false,
            endTime: "",
            isLoading: false,
            shadeOn: false,
            cropperOn: false,
            imgFile: {
                src: null,
                type: null
            },
            editData: {
                gameName: "",
                company: "",
                img: "",
                endTime: "",
                description: "",
                platform: [],
                behavior: [],
                genre: [],
                qaLead: {
                    name: "",
                    workPhone: "",
                    mobile: "",
                    email: "",
                }
            },
            // Tag selections
            platformSelected: [],
            behaviorSelected: [],
            genreSelected: [],
            // Tag options
            platformOptions: ["Mobile", "Game Console", "PC/Laptop", "Handheld", "VR"],
            behaviorOptions: ["Competition", "Completion", "Community/Social", "Design", "Destruction", "Fantasy", "Story"],
            genreOptions: ["Action", "Adventure", "Casual/Puzzle", "Multiplayer Online", "Role Playing Game", "Shooter", "Simulation", "Sports", "Strategy"],
            
            assignYearSelected: "",
            assignMonthSelected: "",
            assignDaySelected: "",
            assignHourSelected: 0,
            assignYearSelection: [],
            assignDaySelection: [],
            assignHourSelection: [
                { timeText: "0:00 AM", timeString: "00:00:00" },
                { timeText: "1:00 AM", timeString: "01:00:00" },
                { timeText: "2:00 AM", timeString: "02:00:00" },
                { timeText: "3:00 AM", timeString: "03:00:00" },
                { timeText: "4:00 AM", timeString: "04:00:00" },
                { timeText: "5:00 AM", timeString: "05:00:00" },
                { timeText: "6:00 AM", timeString: "06:00:00" },
                { timeText: "7:00 AM", timeString: "07:00:00" },
                { timeText: "8:00 AM", timeString: "08:00:00" },
                { timeText: "9:00 AM", timeString: "09:00:00" },
                { timeText: "10:00 AM", timeString: "10:00:00" },
                { timeText: "11:00 AM", timeString: "11:00:00" },
                { timeText: "12:00 PM", timeString: "12:00:00" },
                { timeText: "1:00 PM", timeString: "13:00:00" },
                { timeText: "2:00 PM", timeString: "14:00:00" },
                { timeText: "3:00 PM", timeString: "15:00:00" },
                { timeText: "4:00 PM", timeString: "16:00:00" },
                { timeText: "5:00 PM", timeString: "17:00:00" },
                { timeText: "6:00 PM", timeString: "18:00:00" },
                { timeText: "7:00 PM", timeString: "19:00:00" },
                { timeText: "8:00 PM", timeString: "20:00:00" },
                { timeText: "9:00 PM", timeString: "21:00:00" },
                { timeText: "10:00 PM", timeString: "22:00:00" },
                { timeText: "11:00 PM", timeString: "23:00:00" }
            ],
            assignMonthSelection: [
                { monthText: "January", monthString: "01" },
                { monthText: "February", monthString: "02" },
                { monthText: "March", monthString: "03" },
                { monthText: "April", monthString: "04" },
                { monthText: "May", monthString: "05" },
                { monthText: "June", monthString: "06" },
                { monthText: "July", monthString: "07" },
                { monthText: "August", monthString: "08" },
                { monthText: "September", monthString: "09" },
                { monthText: "October", monthString: "10" },
                { monthText: "November", monthString: "11" },
                { monthText: "December", monthString: "12" }
            ],
        }
    },
    computed: {
        themeColor() {
            return '#FC7727';
        },
        themeColorLight() {
            return 'rgba(252, 119, 39, 0.3)';
        }
    },
    mounted() {
        let end = new Date(this.detail.endTime);
        this.endTime = end.toDateString();
        
        if (window.localStorage.getItem("userType")) {
            this.userType = parseInt(window.localStorage.getItem("userType"));
        } else {
            this.$router.push({
                name: 'login',
            })
        }

        // Initialize date selections
        let curYear = new Date().getFullYear();
        for (let i = 0; i < 10; i++) {
            this.assignYearSelection.push({yearText: (curYear + i).toString()})
        }

        // Parse the endTime from detail
        this.parseEndTime();
        
        // Initialize edit data with detail data
        this.resetEditData();

        this.updateColor();
    },
    methods: {
        // Helper methods to safely access qaLead properties
        getQaLeadName() {
            return this.detail.qaLead && this.detail.qaLead.name ? this.detail.qaLead.name : 'N/A';
        },
        getQaLeadEmail() {
            return this.detail.qaLead && this.detail.qaLead.email ? this.detail.qaLead.email : 'N/A';
        },
        getQaLeadWorkPhone() {
            return this.detail.qaLead && this.detail.qaLead.workPhone ? this.detail.qaLead.workPhone : 'N/A';
        },
        getQaLeadMobile() {
            return this.detail.qaLead && this.detail.qaLead.mobile ? this.detail.qaLead.mobile : 'N/A';
        },

        parseEndTime() {
            if (this.detail.endTime) {
                const endDate = new Date(this.detail.endTime);
                this.assignYearSelected = endDate.getFullYear().toString();
                this.assignMonthSelected = (endDate.getMonth() + 1).toString().padStart(2, '0');
                this.assignDaySelected = endDate.getDate().toString().padStart(2, '0');
                this.assignHourSelected = endDate.getHours();

                // Update day selection based on month/year
                let totalDays = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
                this.assignDaySelection = [];
                for (let i = 1; i <= totalDays; i++) {
                    let date = i.toString();
                    if (i < 10) {
                        date = "0" + date;
                    }
                    this.assignDaySelection.push({dayText: date});
                }
            }
        },

        resetEditData() {
            this.editData = {
                gameName: this.detail.gameName || "",
                company: this.detail.company || "",
                img: this.detail.img || "",
                endTime: this.detail.endTime || "",
                description: this.detail.description || "",
                platform: this.detail.platform ? [...this.detail.platform] : [],
                behavior: this.detail.behavior ? [...this.detail.behavior] : [],
                genre: this.detail.genre ? [...this.detail.genre] : [],
                qaLead: {
                    name: this.detail.qaLead && this.detail.qaLead.name ? this.detail.qaLead.name : "",
                    workPhone: this.detail.qaLead && this.detail.qaLead.workPhone ? this.detail.qaLead.workPhone : "",
                    mobile: this.detail.qaLead && this.detail.qaLead.mobile ? this.detail.qaLead.mobile : "",
                    email: this.detail.qaLead && this.detail.qaLead.email ? this.detail.qaLead.email : "",
                }
            };
            
            // Set tag selections
            this.platformSelected = this.detail.platform ? [...this.detail.platform] : [];
            this.behaviorSelected = this.detail.behavior ? [...this.detail.behavior] : [];
            this.genreSelected = this.detail.genre ? [...this.detail.genre] : [];
        },

        handleModify() {
            this.modify = true;
            this.resetEditData();
            this.parseEndTime();
        },

        handleCancel() {
            const answer = window.confirm('Do you really want to cancel? All unsaved changes will be lost!');
            if (answer) {
                this.modify = false;
                this.resetEditData();
            }
        },

        handleSave() {
            // Validation
            if (!this.editData.gameName || this.editData.gameName.trim() === "") {
                alert('Please fill in required field: Game Name');
                return;
            }
            
            if (!this.editData.company || this.editData.company.trim() === "") {
                alert('Please fill in required field: Company');
                return;
            }

            // Build endTime string
            this.editData.endTime = this.assignYearSelected + "-" + this.assignMonthSelected + "-" + this.assignDaySelected + "T" + this.assignHourSelection[this.assignHourSelected].timeString;

            // Add tags to data object
            this.editData.platform = [...this.platformSelected];
            this.editData.behavior = [...this.behaviorSelected];
            this.editData.genre = [...this.genreSelected];

            this.isLoading = true;
            
            // Call the API to update the project
            GamesObject.createProject(
                this.detail.gameId,
                this.detail.developerID,
                this.editData.gameName,
                this.editData.company,
                this.editData.img,
                this.editData.endTime,
                this.editData.description,
                this.editData.qaLead,
                this.editData.platform,
                this.editData.behavior,
                this.editData.genre
            )
            .then(() => {
                console.log('Game updated successfully');
                this.isLoading = false;
                this.modify = false;
                
                // Emit event to parent to refresh data
                this.$emit('confirmed');
                
                alert('Game updated successfully!');
            })
            .catch((error) => {
                console.log('Error updating game:', error);
                this.isLoading = false;
                alert('Error updating game. Please try again.');
            });
        },

        toggleTag(category, tag) {
            if (category === 'platform') {
                const index = this.platformSelected.indexOf(tag);
                if (index > -1) {
                    this.platformSelected.splice(index, 1);
                } else {
                    this.platformSelected.push(tag);
                }
            } else if (category === 'behavior') {
                const index = this.behaviorSelected.indexOf(tag);
                if (index > -1) {
                    this.behaviorSelected.splice(index, 1);
                } else {
                    this.behaviorSelected.push(tag);
                }
            } else if (category === 'genre') {
                const index = this.genreSelected.indexOf(tag);
                if (index > -1) {
                    this.genreSelected.splice(index, 1);
                } else {
                    this.genreSelected.push(tag);
                }
            }
        },

        triggerFileInput() {
            if (this.modify) {
                this.$refs.fileInput.click();
            }
        },

        uploadImg(event) {
            const { files } = event.target;
            if (files && files[0]) {
                if (this.imgFile.src) {
                    URL.revokeObjectURL(this.imgFile.src)
                }
                const blob = URL.createObjectURL(files[0]);
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.imgFile = {
                        src: blob,
                        type: getMimeType(e.target.result, files[0].type),
                    };
                };
                reader.readAsArrayBuffer(files[0]);
            }
            this.cropperOn = true;
        },

        handleCropperConfirm(imgBlob) {
            this.cropperOn = false;
            const reader = new FileReader();
            reader.readAsDataURL(imgBlob);
            reader.onload = () => {
                this.editData.img = reader.result;
            };
        },

        handleCropperCancel() {
            this.cropperOn = false;
        },

        handleSelect(box, event) {
            const idx = event.target.selectedIndex;
            if (box == "year") {
                this.assignYearSelected = this.assignYearSelection[idx].yearText;
                if (this.assignMonthSelected != "") {
                    let totalDays = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
                    this.assignDaySelection = [];
                    for (let i = 1; i <= totalDays; i++) {
                        let date = i.toString();
                        if (i < 10) {
                            date = "0" + date;
                        }
                        this.assignDaySelection.push({dayText: date});
                    }
                }
            } else if (box == "month") {
                this.assignMonthSelected = this.assignMonthSelection[idx].monthString;
                if (this.assignYearSelected != "") {
                    let totalDays = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
                    this.assignDaySelection = [];
                    for (let i = 1; i <= totalDays; i++) {
                        let date = i.toString();
                        if (i < 10) {
                            date = "0" + date;
                        }
                        this.assignDaySelection.push({dayText: date});
                    }
                }
            } else if (box == "day") {
                this.assignDaySelected = this.assignDaySelection[idx].dayText;
            } else {
                this.assignHourSelected = idx;
            }
        },

        updateColor() {
            var r = document.querySelector(':root');
            r.style.setProperty('--GAMEDM', this.$store.state.gamedm);
            r.style.setProperty('--THEME-COLOR', this.themeColor);
            r.style.setProperty('--THEME-COLOR-LIGHT', this.themeColorLight);
        },
    },
    beforeRouteLeave(to, from, next) {
        if (this.modify) {
            const answer = window.confirm('Do you really want to leave? All unsaved changes will be lost!')
            if (answer) {
                next();
            } else {
                next(false);
            }
        } else {
            next();
        }
    },
    destroyed() {
        if (this.imgFile.src) {
            URL.revokeObjectURL(this.imgFile.src)
        }
    }
}
</script>

<style scoped>
.gda-survey-box {
  background-color: #fff;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  height: 75vh;
  margin: 0 2%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
}

/* Scrollbar styles inherited from global App.vue */

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  position: relative;
}

/* Header row with title and buttons aligned */
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--THEME-COLOR, #FC7727);
}

.section-header-inline {
  font-size: 24px;
  font-weight: 600;
  color: var(--THEME-COLOR, #FC7727);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 15px;
  flex-shrink: 0;
}

.section-header {
  font-size: 24px;
  font-weight: 600;
  margin: 30px 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--THEME-COLOR, #FC7727);
  color: var(--THEME-COLOR, #FC7727);
}

.fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.field-card {
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.field-card.full-width {
  grid-column: 1 / -1;
}

.field-card:hover {
  border-color: var(--THEME-COLOR, #FC7727);
  box-shadow: 0 4px 16px var(--THEME-COLOR-LIGHT, rgba(252, 119, 39, 0.3));
  transform: translateY(-2px);
}

.field-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
  display: flex;
}
.field-label > div {
    margin-left: auto; 
}
.field-label .action-btn{
    padding:6px 12px;
    min-width: 10px;
}
.action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    padding-top: 30px;
    border-top: 1px solid #eee;
    margin-top: auto;
}

.field-input {
  border: 1px solid #ddd;
  outline: none;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 16px;
  color: #333;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
}

.field-input:focus {
  border-color: var(--THEME-COLOR, #FC7727);
  box-shadow: 0 0 0 3px var(--THEME-COLOR-LIGHT, rgba(252, 119, 39, 0.1));
}

.field-input::placeholder {
  color: #999;
}

.field-display {
  font-size: 16px;
  color: #333;
  padding: 8px 0;
  min-height: 24px;
}

.field-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.image-upload-card {
  min-height: auto;
}

.image-upload-section {
  width: 100%;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(249, 249, 249, 0.5);
  position: relative;
}

.upload-area:hover {
  border-color: var(--THEME-COLOR, #FC7727);
  background: rgba(252, 119, 39, 0.05);
}

.image-display {
  text-align: center;
  padding: 20px;
}

.image-preview-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.preview-image {
  max-width: 300px;
  max-height: 300px;
  border-radius: 8px;
  display: block;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 500;
}

/* Date Selection */
.date-selection-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
}

.date-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-item label {
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.date-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: border-color 0.3s ease;
}

.date-select:focus {
  outline: none;
  border-color: var(--THEME-COLOR, #FC7727);
}

/* Tags Section */
.tags-section {
  margin-bottom: 30px;
}

.tag-category {
  margin-bottom: 25px;
}

.tag-category-header {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.tag-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-btn {
  padding: 8px 16px;
  border: 2px solid #e0e0e0;
  background: white;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  color: #4b4b4d;
  font-weight: 500;
}

.tag-btn:hover:not(.disabled) {
  border-color: var(--THEME-COLOR, #FC7727);
  background: rgba(252, 119, 39, 0.05);
}

.tag-btn.selected {
  background: var(--THEME-COLOR, #FC7727);
  color: white;
  border-color: var(--THEME-COLOR, #FC7727);
}

.tag-btn.disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

/* Action Buttons */
.action-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: white;
  min-width: 120px;
  white-space: nowrap;
}

.modify-btn,
.confirm-btn {
  background-color: var(--THEME-COLOR, #FC7727);
}

.cancel-btn {
  background-color: #999;
}

.modify-btn:hover,
.confirm-btn:hover {
  background: #FF8C00;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--THEME-COLOR-LIGHT, rgba(252, 119, 39, 0.3));
}

.cancel-btn:hover {
  background: #777;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Loading overlay */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .gda-survey-box {
    padding: 15px;
  }
  
  .header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .header-actions {
    width: 100%;
    flex-direction: column;
  }
  
  .action-btn {
    width: 100%;
  }
  
  .fields-grid {
    grid-template-columns: 1fr;
  }
  
  .section-header-inline,
  .section-header {
    font-size: 20px;
  }
  
  .upload-area {
    padding: 30px 20px;
  }
  
  .date-selection-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 1024px) {
  .fields-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
}
</style>


// WEBPACK FOOTER //
// src/components/game-detail/GameDetail-Description.vue